import React, { useEffect, useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ReferencesSlider from '../ReferencesSlider';
import ConsultationForm from '../ConsultationForm';
import { API_URLS } from '../../config/api';
import DOMPurify from 'dompurify';

const DEFAULT_CONTENT = {
  hero: {
    badgeText: 'Website Al',
    badgeIcon: 'Rocket',
    title: 'Dijital Dönüşüm Ortağınız',
    description:
      "Türkiye'nin en kapsamlı B2B dijital hizmet platformu. İşinizi teknolojiyle buluşturuyor, büyümenize ivme kazandırıyoruz.",
    button1: { text: 'İletişime Geç', link: '/iletisim', icon: 'Mail' },
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1000&q=80'
  },
  mission: {
    badge_text: 'Misyon',
    title: 'İşletmeleri dijital dünyada güçlendirmek',
    description:
      'Kaliteli freelance hizmetlerini erişilebilir kılarak, teknoloji ile iş dünyasını buluşturuyoruz. Her ölçekten işletmenin büyümesine katkı sağlıyoruz.',
    image_url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=900&q=80'
  },
  vision: {
    badge_text: 'Vizyon',
    title: 'Bölgenin en büyük dijital hizmetler platformu olmak',
    description:
      "2030'a kadar 1 milyon freelancer ve 500 bin işletmeyi bir araya getirmek; inovasyon ve güvenin adresi olmak.",
    image_url: 'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=900&q=80'
  },
  values: [
    {
      icon: 'Shield',
      title: 'Güven',
      description: 'Escrow ve şeffaf süreçlerle güvenli çalışma.',
      gradient: 'from-[#7A5AFF] to-[#5a3fd9]'
    },
    {
      icon: 'Rocket',
      title: 'Kalite',
      description: 'Doğrulanmış uzmanlar ve kalite güvencesi.',
      gradient: 'from-[#7A5AFF] to-[#5a3fd9]'
    },
    {
      icon: 'Target',
      title: 'Sonuç',
      description: 'Ölçülebilir başarı ve sürdürülebilir büyüme.',
      gradient: 'from-[#7A5AFF] to-[#5a3fd9]'
    }
  ],
  timeline: {
    badge_text: 'Yolculuğumuz',
    title: 'Kısa Tarihçemiz',
    items: [
      {
        year: '2020',
        title: 'Kuruluş',
        description: 'Website Al, Türkiye’de KOBİ’lere odaklı dijital hizmet platformu olarak kuruldu.'
      },
      {
        year: '2022',
        title: 'Büyüme',
        description: '25.000+ freelancer ve 100.000+ proje eşiğini geçtik; yeni hizmet vertical’leri eklendi.'
      },
      {
        year: '2024',
        title: 'Enterprise',
        description: 'Kurumsal çözümler ve AI destekli ürünlerle bölgesel genişleme başladı.'
      }
    ]
  },
  services: {
    badge_text: 'Hizmetlerimiz',
    title: 'İşinizi Büyüten Ana Çözümlerimiz',
    description:
      'Uçtan uca dijital dönüşüm için SEO’dan reklam yönetimine, e-ticaret çözümlerinden hazır web paketlerine kadar her adımda yanınızdayız.',
    cards: [
      {
        icon: 'Search',
        title: 'SEO Hizmetleri',
        description: 'Organik görünürlüğünüzü artıran kapsamlı SEO stratejileriyle büyümenizi hızlandırıyoruz.',
        link: '/seo-hizmetleri'
      },
      {
        icon: 'Megaphone',
        title: 'Reklam Yönetimi',
        description: 'Google, Meta ve LinkedIn kampanyalarınızı performans odaklı şekilde ölçeklendiriyoruz.',
        link: '/reklam-yonetimi'
      },
      {
        icon: 'ShoppingBag',
        title: 'E-Ticaret Çözümleri',
        description: 'Mağazanız için tasarım, geliştirme ve büyüme odaklı uçtan uca çözümler sağlıyoruz.',
        link: '/eticaret'
      },
      {
        icon: 'Laptop2',
        title: 'Hazır Web Sitesi Paketleri',
        description: 'Hızlı kurulumlu, ölçeklenebilir web paketleriyle markanızı dakikalar içinde yayına alın.',
        link: '/hazir-web-sitesi-tasarimlari'
      }
    ]
  },
  references: {
    badge_text: 'Referanslar',
    title: 'Bize Güvenen Referanslarımız',
    show_slider: true,
    show_partner_grid: true
  },
  cta: {
    title: 'Dijital Dönüşümünüzü Başlatın',
    description: 'Uzman ekibimiz ve güvenilir çözümlerimizle dijital dünyada yerinizi alın.',
    primary_button: {
      text: 'Ücretli Danışmanlık',
      action: 'open_consultation_modal',
      icon: 'Lightbulb'
    },
    secondary_button: {
      text: 'İletişim',
      link: '/iletisim',
      variant: 'outline'
    }
  }
};

const resolveIcon = (name, fallback = 'Circle') => {
  if (name && Icons[name]) {
    return Icons[name];
  }
  return Icons[fallback] || Icons.Circle;
};

const AboutPage = () => {
  const [partners, setPartners] = useState([]);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);

  const apiBase = useMemo(
    () => (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : ''),
    []
  );

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const res = await fetch(API_URLS.PARTNERS);
        if (!res.ok) return;
        const data = await res.json();
        setPartners(Array.isArray(data) ? data : data.data || []);
      } catch (error) {
        console.error('Partner listesi yüklenemedi', error);
      }
    };
    loadPartners();
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=hakkimizda`);
        const data = await res.json();
        if (data.status === 'success') {
          setPageContent(data.data);
        } else {
          setPageContent(null);
        }
      } catch (error) {
        console.error('Hakkımızda içeriği yüklenemedi:', error);
        setPageContent(null);
      } finally {
        setLoading(false);
        setTimeout(() => setReady(true), 120);
      }
    };
    fetchContent();
  }, [apiBase]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#7A5AFF]" />
      </div>
    );
  }

  const merged = {
    hero: {
      ...DEFAULT_CONTENT.hero,
      ...(pageContent || {})
    },
    mission: pageContent?.additional_sections?.mission || DEFAULT_CONTENT.mission,
    vision: pageContent?.additional_sections?.vision || DEFAULT_CONTENT.vision,
    values:
      (Array.isArray(pageContent?.features_section) && pageContent.features_section.length > 0
        ? pageContent.features_section
        : DEFAULT_CONTENT.values),
    timeline: {
      ...DEFAULT_CONTENT.timeline,
      ...(pageContent?.additional_sections?.timeline || {})
    },
    services: {
      ...DEFAULT_CONTENT.services,
      ...(pageContent?.additional_sections?.services || {})
    },
    references: {
      ...DEFAULT_CONTENT.references,
      ...(pageContent?.additional_sections?.references || {})
    },
    cta: {
      ...DEFAULT_CONTENT.cta,
      ...(pageContent?.additional_sections?.cta || {})
    }
  };

  const heroButtons = [];
  if (merged.hero.hero_button1_text || merged.hero.button1?.text) {
    heroButtons.push({
      text: merged.hero.hero_button1_text || merged.hero.button1?.text,
      link: merged.hero.hero_button1_link || merged.hero.button1?.link || '#',
      icon: merged.hero.hero_button1_icon || merged.hero.button1?.icon || 'Mail'
    });
  }
  if (merged.hero.hero_button2_text) {
    heroButtons.push({
      text: merged.hero.hero_button2_text,
      link: merged.hero.hero_button2_link || '#',
      icon: merged.hero.hero_button2_icon || 'ArrowRight'
    });
  }

  const handlePrimaryCta = (event, button) => {
    if (button?.action === 'open_consultation_modal') {
      event.preventDefault();
      setShowConsultationForm(true);
    }
  };

  const HeroBadgeIcon = resolveIcon(merged.hero.hero_badge_icon || merged.hero.badgeIcon, 'Rocket');

  return (
    <div
      className="min-h-screen bg-[#f5f3ff]/30"
      style={{
        opacity: ready ? 1 : 0,
        transform: ready ? 'none' : 'translateY(24px)',
        transition: 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out'
      }}
    >
      <section className="bg-gradient-to-br from-black via-[#0f0a1f] to-[#1a0a2e] py-20 text-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-start">
            <div className="w-full text-left lg:w-1/2">
              <div className="mb-6 inline-flex items-center space-x-3 rounded-full bg-[#7A5AFF]/20 px-6 py-3 backdrop-blur">
                <HeroBadgeIcon className="h-5 w-5 text-[#7A5AFF]" />
                <span>{merged.hero.hero_badge_text || merged.hero.badgeText}</span>
              </div>
              <h1
                className="mb-6 text-4xl font-bold leading-tight md:text-6xl"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(merged.hero.hero_title || merged.hero.title || '')
                }}
              />
              <p
                className="mb-8 text-lg leading-relaxed text-white/85"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(merged.hero.hero_description || merged.hero.description || '')
                }}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {heroButtons.map((btn, idx) => {
                  const BtnIcon = resolveIcon(btn.icon, idx === 0 ? 'Mail' : 'ArrowRight');
                  const isPrimary = idx === 0;
                  return (
                    <Button
                      key={`${btn.text}-${idx}`}
                      size="lg"
                      asChild
                      className={
                        isPrimary
                          ? 'bg-white text-[#7A5AFF] hover:bg-[#7A5AFF]/10 px-8 py-3 font-semibold'
                          : 'bg-white/10 text-white hover:bg-white/20 px-8 py-3 font-semibold'
                      }
                    >
                      <a href={btn.link} className="inline-flex items-center gap-2">
                        <BtnIcon className="h-5 w-5" />
                        {btn.text}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="flex w-full justify-center lg:w-1/2 lg:justify-end">
              <div className="relative w-full max-w-xl">
                <div className="absolute -inset-4 rounded-3xl bg-white/10 blur-3xl" />
                <img
                  src={merged.hero.hero_image_url || merged.hero.image}
                  alt="Hakkımızda"
                  className="relative w-full rounded-3xl border border-white/10 object-cover shadow-2xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto space-y-10 px-6 py-16">
        <MissionVisionSection mission={merged.mission} vision={merged.vision} />
      </div>

      <ValuesSection values={merged.values} />

      <TimelineSection timeline={merged.timeline} />

      <ServicesSection services={merged.services} />

      <ReferencesSection references={merged.references} partners={partners} />

      <CtaSection
        cta={merged.cta}
        onPrimaryClick={handlePrimaryCta}
      />

      {showConsultationForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto">
            <ConsultationForm isModal onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

const MissionVisionSection = ({ mission, vision }) => {
  const MissionIcon = resolveIcon(mission.badge_icon, 'BadgeCheck');
  const VisionIcon = resolveIcon(vision.badge_icon, 'Eye');

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div>
          <Badge className="mb-4 bg-[#7A5AFF]/15 text-[#7A5AFF]">
            <span className="inline-flex items-center gap-2">
              <MissionIcon className="h-4 w-4" />
              {mission.badge_text || DEFAULT_CONTENT.mission.badge_text}
            </span>
          </Badge>
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            {mission.title || DEFAULT_CONTENT.mission.title}
          </h2>
          <p className="leading-relaxed text-gray-600">
            {mission.description || DEFAULT_CONTENT.mission.description}
          </p>
        </div>
        {mission.image_url && (
          <div className="flex justify-center">
            <img
              src={mission.image_url}
              alt="Misyon"
              className="max-w-lg rounded-3xl object-cover shadow-md"
              loading="lazy"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
        {vision.image_url && (
          <div className="order-2 flex justify-center lg:order-1">
            <img
              src={vision.image_url}
              alt="Vizyon"
              className="max-w-lg rounded-3xl object-cover shadow-md"
              loading="lazy"
            />
          </div>
        )}
        <div className="order-1 lg:order-2">
          <Badge className="mb-4 bg-[#7A5AFF]/15 text-[#7A5AFF]">
            <span className="inline-flex items-center gap-2">
              <VisionIcon className="h-4 w-4" />
              {vision.badge_text || DEFAULT_CONTENT.vision.badge_text}
            </span>
          </Badge>
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            {vision.title || DEFAULT_CONTENT.vision.title}
          </h2>
          <p className="leading-relaxed text-gray-600">
            {vision.description || DEFAULT_CONTENT.vision.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const ValuesSection = ({ values }) => (
  <div className="container mx-auto px-6 pb-16">
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {values.map((value, idx) => {
        const Icon = resolveIcon(value.icon, 'Shield');
        return (
          <Card key={`${value.title}-${idx}`} className="shadow-lg">
            <CardContent className="p-8 text-center">
              <div
                className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${
                  value.gradient || 'from-[#7A5AFF] to-[#5a3fd9]'
                }`}
              >
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-2 text-xl font-bold">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </div>
);

const TimelineSection = ({ timeline }) => (
  <div className="container mx-auto px-6 pb-16">
    <div className="mb-10 text-center">
      <Badge className="mb-3 bg-gray-100 text-gray-700">
        {timeline.badge_text || DEFAULT_CONTENT.timeline.badge_text}
      </Badge>
      <h2 className="text-2xl font-bold md:text-3xl">
        {timeline.title || DEFAULT_CONTENT.timeline.title}
      </h2>
    </div>
    <div className="relative ml-4 space-y-8 border-l-2 border-gray-200">
      {(timeline.items || DEFAULT_CONTENT.timeline.items).map((item, idx) => (
        <div key={`${item.year}-${idx}`} className="ml-4">
          <div className="absolute -left-[9px] h-4 w-4 rounded-full bg-gradient-to-br from-[#7A5AFF] to-[#5a3fd9]" />
          <Card className="shadow-md">
            <CardContent className="flex items-start gap-4 p-5">
              <div className="font-bold text-[#7A5AFF]">{item.year}</div>
              <div>
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  </div>
);

const ServicesSection = ({ services }) => (
  <div className="container mx-auto px-6 pb-16">
    <div className="mb-12 text-center">
      {services.badge_text && (
        <Badge className="mb-3 bg-[#7A5AFF]/15 text-[#7A5AFF]">{services.badge_text}</Badge>
      )}
      <h2 className="text-2xl font-bold md:text-3xl">
        {services.title || DEFAULT_CONTENT.services.title}
      </h2>
      {services.description && (
        <p className="mx-auto mt-4 max-w-2xl text-gray-600">{services.description}</p>
      )}
    </div>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {(services.cards || DEFAULT_CONTENT.services.cards).map((card, idx) => {
        const Icon = resolveIcon(card.icon, 'Sparkles');
        return (
          <Card
            key={`${card.title}-${idx}`}
            className="border border-[#7A5AFF]/20 shadow-lg transition-all duration-300 hover:border-[#7A5AFF]/50"
          >
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#7A5AFF] to-[#5a3fd9] text-white shadow-md">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">{card.title}</h3>
                  <p className="mb-4 leading-relaxed text-gray-600">{card.description}</p>
                  {card.link && (
                    <a
                      href={card.link}
                      className="inline-flex items-center font-semibold text-[#7A5AFF] transition-colors hover:text-[#5a3fd9]"
                    >
                      Daha fazla bilgi al
                      <Icons.ChevronRight className="ml-1 h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </div>
);

const ReferencesSection = ({ references, partners }) => {
  const { badge_text, title, show_partner_grid, show_slider } = references;
  return (
    <div className="container mx-auto px-6 pb-16">
      <div className="mb-8 text-center">
        {badge_text && (
          <Badge className="mb-3 bg-gray-100 text-gray-700">{badge_text}</Badge>
        )}
        {title && <h2 className="text-2xl font-bold md:text-3xl">{title}</h2>}
      </div>
      {show_partner_grid !== false && partners.length > 0 && (
        <div className="mb-12 grid grid-cols-2 items-center gap-6 opacity-90 md:grid-cols-6">
          {partners.map((partner) => (
            <a
              key={partner.id}
              href={partner.link_url || '#'}
              target={partner.link_url ? '_blank' : '_self'}
              rel="noreferrer"
              className="flex items-center justify-center"
            >
              <img
                src={partner.logo_url}
                alt={partner.name}
                className="h-10 object-contain grayscale transition hover:grayscale-0"
              />
            </a>
          ))}
        </div>
      )}
      {show_slider !== false && (
        <div className="relative left-1/2 right-1/2 -ml-[50vw] w-screen overflow-hidden">
          <ReferencesSlider />
        </div>
      )}
    </div>
  );
};

const CtaSection = ({ cta, onPrimaryClick }) => {
  const primaryButton = cta.primary_button || {};
  const secondaryButton = cta.secondary_button || {};
  const PrimaryIcon = resolveIcon(primaryButton.icon, 'Lightbulb');

  return (
    <section className="bg-gradient-to-r from-black via-[#1a0a2e] to-[#7A5AFF] py-20 text-white">
      <div className="container mx-auto px-6 text-center">
        <h2 className="mb-6 text-4xl font-bold md:text-5xl">{cta.title}</h2>
        <p className="mx-auto mb-12 max-w-3xl text-xl text-white/85">{cta.description}</p>
        <div className="flex flex-col items-center justify-center gap-6 sm:flex-row">
          {primaryButton.text && (
            <Button
              size="lg"
              className="bg-[#7A5AFF] px-12 py-4 text-xl font-bold text-white hover:bg-[#8b6bff] shadow-lg shadow-[#7A5AFF]/40"
              onClick={(event) => onPrimaryClick(event, primaryButton)}
            >
              <PrimaryIcon className="mr-3 h-6 w-6" />
              {primaryButton.text}
            </Button>
          )}
          {secondaryButton.text && (
            <Button
              size="lg"
              variant={secondaryButton.variant || 'outline'}
              className="border-white px-12 py-4 text-xl text-white hover:bg-white hover:text-[#7A5AFF]"
              asChild
            >
              <a href={secondaryButton.link || '#'}>{secondaryButton.text}</a>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AboutPage;

